/**
 * TeamTab — Super-admin view for managing sub-admin accounts.
 * Only visible to super_admin role.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { API_BASE, getAdminToken, formatDate } from './adminUtils';
import {
  Users, UserPlus, RefreshCw, Trash2, ToggleLeft, ToggleRight,
  KeyRound, Shield, ChevronDown,
} from 'lucide-react';

// ── Role metadata ──────────────────────────────────────────────────────────

const ROLES = [
  { value: 'jobs_manager', label: 'Jobs Manager',  color: 'text-blue-400',   desc: 'Jobs tab only' },
  { value: 'support',      label: 'Support',        color: 'text-emerald-400',desc: 'Users, Tickets, Agents' },
  { value: 'finance',      label: 'Finance',        color: 'text-amber-400',  desc: 'Payments, Revenue, Enroll' },
  { value: 'content',      label: 'Content',        color: 'text-purple-400', desc: 'Courses, Blueprint, Broadcast…' },
  { value: 'analytics',    label: 'Analytics',      color: 'text-cyan-400',   desc: 'Analytics, Email Activity…' },
];

const roleStyle = (role) =>
  ROLES.find((r) => r.value === role) || { label: role, color: 'text-slate-400', desc: '' };

// ── Small sub-components ────────────────────────────────────────────────────

const Badge = ({ role }) => {
  const { label, color } = roleStyle(role);
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-700/60 ${color}`}>
      {label}
    </span>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

export const TeamTab = () => {
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetting, setResetting]   = useState(null); // member id being reset
  const adminToken = getAdminToken();

  // ── Create form state ─────────────────────────────────────────────────────
  const EMPTY_FORM = { username: '', password: '', name: '', role: 'support' };
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // ── Reset-password form ──────────────────────────────────────────────────
  const [resetForm, setResetForm]   = useState({ id: null, password: '' });

  const authHeader = () => ({ Authorization: `Bearer ${adminToken}` });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchTeam = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/team`, { headers: authHeader() });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setMembers(data.members || []);
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  // ── Create sub-admin ──────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.name) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/team`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      setForm(EMPTY_FORM);
      setShowCreate(false);
      fetchTeam();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active status ──────────────────────────────────────────────────
  const handleToggle = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/${id}/toggle`, {
        method: 'PATCH',
        headers: authHeader(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      fetchTeam();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id, username) => {
    if (!window.confirm(`Permanently delete "${username}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/${id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      fetchTeam();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ── Reset password ────────────────────────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setResetting(resetForm.id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/${resetForm.id}/password`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: resetForm.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Password updated successfully');
      setResetForm({ id: null, password: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResetting(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-400" />
            Admin Team
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {members.length} sub-admin{members.length !== 1 ? 's' : ''} · you are the <span className="text-orange-400 font-medium">Super Admin</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchTeam}
            className="flex items-center gap-2 bg-slate-700/60 hover:bg-slate-700 text-slate-300 text-sm px-3 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm px-3 py-2 rounded-lg transition-colors"
          >
            <UserPlus className="w-4 h-4" /> Add Member
          </button>
        </div>
      </div>

      {/* Role guide */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {ROLES.map((r) => (
          <div key={r.value} className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3">
            <p className={`text-xs font-bold ${r.color}`}>{r.label}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{r.desc}</p>
          </div>
        ))}
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-slate-800/60 border border-orange-500/30 rounded-2xl p-5 space-y-4"
        >
          <p className="text-sm font-semibold text-orange-400">New Sub-Admin Account</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Username</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="e.g. ravi_jobs"
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Ravi Kumar"
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Password (min 8 chars)</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Strong password"
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Role</label>
              <div className="relative">
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full appearance-none bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500/50"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create Account'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
              className="bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-sm px-5 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      ) : members.length === 0 ? (
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl py-16 text-center">
          <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No sub-admins yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-700/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/50 bg-slate-800/60">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Name / Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Last Login</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {members.map((m) => (
                <React.Fragment key={m.id}>
                  <tr className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-white">{m.name}</p>
                      <p className="text-xs text-slate-500">@{m.username}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge role={m.role} />
                      <p className="text-[10px] text-slate-600 mt-0.5">{roleStyle(m.role).desc}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {m.last_login ? formatDate(m.last_login) : <span className="italic text-slate-600">Never</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {m.is_active ? (
                        <span className="text-xs bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Disabled</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {/* Toggle */}
                        <button
                          onClick={() => handleToggle(m.id)}
                          title={m.is_active ? 'Disable account' : 'Enable account'}
                          className="p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors"
                        >
                          {m.is_active
                            ? <ToggleRight className="w-5 h-5 text-emerald-400" />
                            : <ToggleLeft className="w-5 h-5 text-slate-500" />
                          }
                        </button>
                        {/* Reset password */}
                        <button
                          onClick={() => setResetForm({ id: m.id, password: '' })}
                          title="Reset password"
                          className="p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors"
                        >
                          <KeyRound className="w-4 h-4 text-amber-400" />
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(m.id, m.username)}
                          title="Delete account"
                          className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Inline reset-password row */}
                  {resetForm.id === m.id && (
                    <tr className="bg-amber-500/5">
                      <td colSpan={5} className="px-4 py-3">
                        <form onSubmit={handleResetPassword} className="flex items-center gap-3">
                          <p className="text-xs text-amber-400 font-medium whitespace-nowrap">New password for @{m.username}:</p>
                          <input
                            type="password"
                            value={resetForm.password}
                            onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                            placeholder="Min 8 characters"
                            className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 w-56"
                          />
                          <button
                            type="submit"
                            disabled={!!resetting}
                            className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-4 py-1.5 rounded-lg disabled:opacity-50"
                          >
                            {resetting === m.id ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setResetForm({ id: null, password: '' })}
                            className="text-xs text-slate-400 hover:text-slate-200"
                          >
                            Cancel
                          </button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
